import type {
  PortalTicketStatus,
  PortalTicketType,
  PortalTicketPriority,
} from '@api/generated/client';

export const STATUS_LABELS: Record<PortalTicketStatus, string> = {
  SUBMITTED: 'Submitted',
  PENDING_ADMIN_REVIEW: 'Pending admin review',
  NEEDS_CLARIFICATION: 'Needs clarification',
  DUPLICATE: 'Duplicate',
  RESOLVED_BY_ADMIN: 'Resolved by admin',
  CLOSED: 'Closed',
  PENDING_SUPER_ADMIN_REVIEW: 'Pending admin review',
  SENT_TO_CLEVER_BENEFITS: 'Sent to Clever Benefits',
  SENT_TO_ENGINEERING: 'Sent to Engineering',
  BACKLOG: 'Backlog',
  UPCOMING: 'Upcoming',
  CURRENT_SPRINT: 'Current sprint',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
};

export const STATUS_BADGE_CLASS: Record<PortalTicketStatus, string> = {
  SUBMITTED: 'bg-gray-100 text-gray-700 border-0',
  PENDING_ADMIN_REVIEW: 'bg-amber text-white border-0',
  NEEDS_CLARIFICATION: 'bg-blue-500 text-white border-0',
  DUPLICATE: 'bg-gray-200 text-gray-700 border-0',
  RESOLVED_BY_ADMIN: 'bg-indigo-500 text-white border-0',
  CLOSED: 'bg-emerald text-white border-0',
  PENDING_SUPER_ADMIN_REVIEW: 'bg-orange-500 text-white border-0',
  SENT_TO_CLEVER_BENEFITS: 'bg-purple-500 text-white border-0',
  SENT_TO_ENGINEERING: 'bg-indigo-500 text-white border-0',
  BACKLOG: 'bg-gray-200 text-gray-700 border-0',
  UPCOMING: 'bg-gray-300 text-gray-800 border-0',
  CURRENT_SPRINT: 'bg-blue-500 text-white border-0',
  IN_PROGRESS: 'bg-blue-500 text-white border-0',
  COMPLETED: 'bg-emerald text-white border-0',
};

export const TYPE_LABELS: Record<PortalTicketType, string> = {
  QUESTION: 'Question',
  BUG: 'Bug',
  FEATURE_REQUEST: 'Feature request',
  ACCOUNT_ACCESS: 'Account / access',
  TRAINING: 'Training',
  OTHER: 'Other',
};

export const PRIORITY_LABELS: Record<PortalTicketPriority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  URGENT: 'Urgent',
};

// Statuses past the admin handoff — Phase B's "dev flow" surface shown to
// brokers/admins in the list. amber/blue/emerald/gray/indigo/purple legend
// maps to: waiting / active / done / dormant / handed-to-CB.
export const DEV_FLOW_STATUSES: ReadonlySet<PortalTicketStatus> = new Set([
  'PENDING_SUPER_ADMIN_REVIEW',
  'SENT_TO_CLEVER_BENEFITS',
  'SENT_TO_ENGINEERING',
  'BACKLOG',
  'UPCOMING',
  'CURRENT_SPRINT',
  'IN_PROGRESS',
  'COMPLETED',
]);

export const SEND_BACKABLE: ReadonlySet<PortalTicketStatus> = new Set([
  'SENT_TO_CLEVER_BENEFITS',
  'SENT_TO_ENGINEERING',
  'BACKLOG',
  'UPCOMING',
  'CURRENT_SPRINT',
]);

export const SEND_TO_DEV_FROM: ReadonlySet<PortalTicketStatus> = new Set([
  'SENT_TO_CLEVER_BENEFITS',
]);

export const ENGINEERING_STATUS_OPTIONS = [
  'BACKLOG',
  'UPCOMING',
  'CURRENT_SPRINT',
  'IN_PROGRESS',
  'COMPLETED',
] as const satisfies readonly PortalTicketStatus[];

export const ENGINEERING_STATUS_MANAGEABLE_FROM: ReadonlySet<PortalTicketStatus> = new Set([
  'SENT_TO_ENGINEERING',
  ...ENGINEERING_STATUS_OPTIONS.filter((status) => status !== 'COMPLETED'),
]);
